import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Input, Select, Typography, Button, message, Modal } from 'antd';
import { academyClassApi, academyTeacherApi, academyStudentApi, adminStudentApi } from '@/services/api';

const { Title } = Typography;

interface StudentDetail {
  id: string;
  studentName: string;
  studentId: string;
  academyCode: string;
  academyName: string;
  birthDate: string;
  parentEmail: string;
  className: string;
  teacherName: string;
  classRoomId?: number;
  assignedTeacherId?: number;
  // 가입 상태: 승인대기, 승인, 반려, 재신청
  registrationStatus: 'pending' | 'approved' | 'rejected' | 'reapplied';
  // 학생 상태: 정상, 퇴원
  studentStatus: 'normal' | 'withdrawn';
  applicationDate: string;
  approvalDate: string;
  withdrawnDate?: string;
}

interface StudentBasicInfoProps {
  studentData: StudentDetail;
}

const StudentBasicInfo: React.FC<StudentBasicInfoProps> = ({ studentData }) => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [classOptions, setClassOptions] = useState<{ label: string; value: string }[]>([]);
  const [teacherOptions, setTeacherOptions] = useState<{ label: string; value: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<StudentDetail['studentStatus']>(studentData.studentStatus);
  const [withdrawing, setWithdrawing] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const academyCode = studentData.academyCode;
        if (!academyCode) return;
        const [classRes, teacherRes]: any = await Promise.all([
          academyClassApi.list({ academyCode, page: 0, size: 200 }),
          academyTeacherApi.list({ academyCode, page: 0, size: 200 }),
        ]);
        const classPage = classRes?.data || classRes;
        const teacherPage = teacherRes?.data || teacherRes;
        setClassOptions((classPage?.content || []).map((c: any) => ({ label: c.name, value: String(c.id) })));
        const activeTeachers = (teacherPage?.content || []).filter((t: any) => (t.status === '정상'));
        const options: { label: string; value: string }[] = activeTeachers.map((t: any) => ({ label: t.name, value: String(t.id) }));
        setTeacherOptions(options);

        // 기존 담당 선생님이 활성 목록에 없으면 초기화
        const assignedId = studentData.assignedTeacherId ? String(studentData.assignedTeacherId) : undefined;
        if (assignedId && !options.find((o: { label: string; value: string }) => o.value === assignedId)) {
          form.setFieldsValue({ assignedTeacherId: undefined });
          message.warning('퇴원한 선생님은 선택할 수 없어 초기화했습니다.');
        }
      } catch (e) {
        // ignore option load errors
      }
    };
    loadOptions();
  }, [studentData.academyCode, studentData.assignedTeacherId, form]);

  useEffect(() => {
    setCurrentStatus(studentData.studentStatus);
    form.setFieldsValue({
      studentStatus: studentData.studentStatus,
      withdrawnDate: studentData.withdrawnDate || '',
    });
  }, [studentData.studentStatus, studentData.withdrawnDate, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await academyStudentApi.update(studentData.id, {
        parentEmail: values.parentEmail || null,
        classRoomId: values.classRoomId ? Number(values.classRoomId) : null,
        assignedTeacherId: values.assignedTeacherId ? Number(values.assignedTeacherId) : null,
      });
      message.success('저장되었습니다.');
    } catch (e: any) {
      if (e?.errorFields) return; // validation error
      message.error(e?.response?.data?.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = () => {
    passwordForm.resetFields();
    setPasswordModalOpen(true);
  };

  const handlePasswordChange = async () => {
    try {
      const values = await passwordForm.validateFields();
      setPasswordSaving(true);
      await adminStudentApi.updatePassword(studentData.id, {
        newPassword: values.newPassword,
      });
      message.success('비밀번호가 변경되었습니다.');
      setPasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleWithdraw = () => {
    if (withdrawing || currentStatus === 'withdrawn') {
      return;
    }

    Modal.confirm({
      title: '해당 학생을 퇴원처리 하겠습니까?',
      okText: '퇴원처리',
      cancelText: '취소',
      okType: 'danger',
      centered: true,
      onOk: async () => {
        setWithdrawing(true);
        try {
          await academyStudentApi.withdraw(studentData.id);
          const detail: any = await academyStudentApi.detail(studentData.id);
          const dto = detail?.data || detail;
          const updatedStatus: StudentDetail['studentStatus'] = dto?.status === '퇴원' ? 'withdrawn' : 'normal';
          const updatedWithdrawnDate = dto?.withdrawnAt ? String(dto.withdrawnAt).replace('T', ' ').slice(0, 16) : '';

          setCurrentStatus(updatedStatus);
          form.setFieldsValue({
            studentStatus: updatedStatus,
            withdrawnDate: updatedWithdrawnDate,
          });
          message.success('퇴원 처리되었습니다.');
        } catch (e: any) {
          message.error(e?.response?.data?.message || '퇴원 처리에 실패했습니다.');
        } finally {
          setWithdrawing(false);
        }
      },
    });
  };

  const registrationStatusConfig = {
    pending: { text: '승인대기', color: 'orange' },
    approved: { text: '승인', color: 'green' },
    rejected: { text: '반려', color: 'red' },
    reapplied: { text: '재신청', color: 'blue' }
  };

  const showPasswordControls = currentStatus !== 'withdrawn';

  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>
        기본정보
      </Title>
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...studentData,
          classRoomId: studentData.classRoomId ? String(studentData.classRoomId) : undefined,
          assignedTeacherId: studentData.assignedTeacherId ? String(studentData.assignedTeacherId) : undefined,
          withdrawnDate: studentData.withdrawnDate || '',
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="ID" name="studentId">
              <Input size="large" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="학원코드" name="academyCode">
              <Input size="large" disabled />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="학원명" name="academyName">
              <Input size="large" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="이름" name="studentName">
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="생년월일" name="birthDate">
              <Input size="large" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="학부모 이메일" name="parentEmail">
              <Input size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="클래스" name="classRoomId">
              <Select size="large" placeholder="클래스를 선택하세요" options={classOptions} allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="담당 선생님" name="assignedTeacherId">
              <Select size="large" placeholder="선생님을 선택하세요" options={teacherOptions} allowClear />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="가입상태">
              <Input 
                size="large" 
                disabled 
                value={registrationStatusConfig[(studentData as any).registrationStatus as keyof typeof registrationStatusConfig]?.text || (studentData as any).registrationStatus || '-'}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="학생상태" name="studentStatus">
              <Select size="large" disabled>
                <Select.Option value="normal">정상</Select.Option>
                <Select.Option value="withdrawn">퇴원</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {currentStatus === 'withdrawn' && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="퇴원일" name="withdrawnDate">
                <Input size="large" disabled />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="가입 신청일" name="applicationDate">
              <Input size="large" disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="가입 승인일" name="approvalDate">
              <Input size="large" disabled />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div
        style={{
          display: 'flex',
          justifyContent: showPasswordControls ? 'space-between' : 'flex-end',
          alignItems: 'center',
          marginTop: 16,
          gap: 8,
        }}
      >
        {showPasswordControls && (
          <Button onClick={openPasswordModal}>
            학생 비밀번호 변경
          </Button>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          {currentStatus !== 'withdrawn' && (
            <Button danger onClick={handleWithdraw} loading={withdrawing}>
              퇴원
          </Button>
        )}
        <Button type="primary" onClick={handleSave} loading={saving}>
          저장
        </Button>
        </div>
      </div>

      {showPasswordControls && (
        <Modal
          title="학생 비밀번호 변경"
          open={passwordModalOpen}
          okText="변경"
          cancelText="취소"
          onOk={handlePasswordChange}
          confirmLoading={passwordSaving}
          onCancel={() => {
            if (!passwordSaving) {
              setPasswordModalOpen(false);
              passwordForm.resetFields();
            }
          }}
          destroyOnClose
        >
          <Form form={passwordForm} layout="vertical">
            <Form.Item
              label="새 비밀번호"
              name="newPassword"
              rules={[
                { required: true, message: '새 비밀번호를 입력하세요.' },
                { pattern: /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9]).{8,}$/, message: '비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.' }
              ]}
            >
              <Input.Password size="large" />
            </Form.Item>
            <Form.Item
              label="비밀번호 확인"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: '비밀번호 확인을 입력하세요.' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('비밀번호가 일치하지 않습니다.'));
                  },
                }),
              ]}
            >
              <Input.Password size="large" />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
};

export default StudentBasicInfo; 